import React, { useEffect } from 'react';
import { Form, Input, Button, Alert, Typography, Divider } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const CURRENT_YEAR = new Date().getFullYear();

const Login = () => {
  const { login, loading, error, isAuthenticated } = useAuth();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const onFinish = async (values) => {
    const { username, password } = values;
    const success = await login(username, password);
    if (success) {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 px-4 py-8">
      <div className="w-full max-w-4xl flex shadow-2xl rounded-2xl overflow-hidden ring-1 ring-black/5">
        {/* 左侧品牌区 */}
        <div className="hidden lg:block w-1/2 bg-gradient-to-br from-orange-500 to-amber-600 p-12">
          <div className="h-full flex flex-col justify-between text-white">
            <div>
              <div className="flex items-center mb-6">
                <img
                  src="/assets/hm.png"
                  alt="山西鸿茂贸易有限公司"
                  className="w-20 h-20 mr-4"
                  style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
                />
                <div>
                  <Title level={1} style={{ color: '#fff', marginBottom: 0 }} className="text-3xl">
                    山西鸿茂贸易有限公司
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.85)' }} className="text-lg">
                    HONGMAO TECHNOLOGY
                  </Text>
                </div>
              </div>
              <Title level={2} style={{ color: '#fff' }} className="mb-4">
                亚马逊AI产品管理系统
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                智能管理您的亚马逊产品，提升运营效率
              </Text>
            </div>
            <div className="space-y-4">
              {[
                '智能产品信息生成',
                '自动化流量池管理',
                '数据驱动的决策支持',
              ].map((item, index) => (
                <div key={item} className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-white font-bold">{index + 1}</span>
                  </div>
                  <Text style={{ color: 'rgba(255,255,255,0.92)' }}>{item}</Text>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 右侧登录表单 */}
        <div className="w-full lg:w-1/2 bg-white p-8 sm:p-12">
          <div className="max-w-md mx-auto">
            {/* 移动端品牌条 */}
            <div className="lg:hidden mb-6 rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-3 text-white flex items-center">
              <img src="/assets/hm.png" alt="山西鸿茂贸易有限公司" className="w-10 h-10 mr-3" />
              <div>
                <div className="font-semibold leading-tight">山西鸿茂贸易有限公司</div>
                <div className="text-xs text-orange-100">亚马逊AI产品管理系统</div>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="hidden lg:flex items-center justify-center mb-4">
                <img
                  src="/assets/hm.png"
                  alt="山西鸿茂贸易有限公司"
                  className="w-14 h-14 mr-3"
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
                />
                <div className="text-left">
                  <Title level={2} className="text-gray-800 mb-0">
                    山西鸿茂贸易有限公司
                  </Title>
                  <Text className="text-gray-500 text-sm">HONGMAO TECHNOLOGY</Text>
                </div>
              </div>
              <Title level={3} className="text-gray-700 mb-2">
                欢迎使用AI管理系统
              </Title>
              <Text className="text-gray-500">请输入您的账号和密码</Text>
            </div>

            {error && (
              <Alert
                message="登录失败"
                description={error}
                type="error"
                showIcon
                className="mb-6"
              />
            )}

            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              layout="vertical"
              size="large"
              initialValues={{ username: '', password: '' }}
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input
                  prefix={<UserOutlined className="text-gray-400" />}
                  placeholder="请输入用户名"
                  autoComplete="username"
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="密码"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined className="text-gray-400" />}
                  placeholder="请输入密码"
                  autoComplete="current-password"
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item className="mb-2">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="w-full rounded-lg h-12 text-lg font-medium"
                  loading={loading}
                  style={{
                    background: 'linear-gradient(90deg, #f97316 0%, #d97706 100%)',
                    border: 'none',
                    boxShadow: '0 8px 20px rgba(249, 115, 22, 0.28)',
                  }}
                >
                  登录
                </Button>
              </Form.Item>
            </Form>

            <Divider className="my-8">
              <Text type="secondary" className="text-sm">系统信息</Text>
            </Divider>

            <div className="text-center text-gray-500 text-sm leading-6">
              <p>山西鸿茂贸易有限公司 · 亚马逊AI产品管理系统 v1.0.0</p>
              <p>© {CURRENT_YEAR} 山西鸿茂贸易有限公司 All Rights Reserved</p>
              <p className="text-xs text-gray-400">专业的AI解决方案提供商</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
